import React from 'react';
import { PostHogProvider } from '@posthog/react';
import ReactDOM from 'react-dom/client';

import App from './App';
import reportWebVitals from './reportWebVitals';
import { applyConsent, readConsent } from './shared/analytics/consent';
import { hubspotSink } from './shared/analytics/hubspotSink';
import { isAnalyticsEnabled, posthog } from './shared/analytics/posthog';
import { posthogSink } from './shared/analytics/posthogSink';
import { registerSink } from './shared/analytics/sinks';
import { IS_MOCK_SESSION } from './shared/dev/isMockSession';

import './index.css';

// Wire up the `track()` destinations before anything can emit an event. Done
// here rather than at module scope in analytics/track.ts so that module stays
// free of SDK imports and its tests can inject fakes.
registerSink(posthogSink);
registerSink(hubspotSink);


// Re-apply a returning visitor's stored choice synchronously at boot, before the
// first React render. PostHog was initialised opted-out (see posthog.ts) and the
// HubSpot pixel is not injected at all until this runs; this is what restores a
// prior "accept" without the visitor waiting for — or re-seeing — the banner. A
// visitor with no stored record stays opted out until they choose in the banner.
//
// Must come after the registerSink calls above: applying consent can emit the
// entry pageview, and a sink registered afterwards would miss it.
const storedConsent = readConsent();
if (storedConsent) {
  applyConsent(storedConsent.categories);
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const tree = isAnalyticsEnabled ? (
  <PostHogProvider client={posthog}>
    <App />
  </PostHogProvider>
) : (
  <App />
);

/**
 * Local mock session — see src/shared/dev/isMockSession.ts.
 *
 * IS_MOCK_SESSION is the literal `false` in a production build, so this branch
 * and the dynamic import inside it are removed by the bundler and the mock API
 * is never shipped. In production this resolves immediately.
 *
 * The render is gated on the install rather than fired alongside it: the mock
 * module is a separate request in dev, so a fire-and-forget install would let
 * the first page's data fetches escape to the real API before the patch landed.
 */
const bootstrap = async () => {
  if (IS_MOCK_SESSION) {
    const { installMockApi } = await import('./shared/dev/mockSession');
    installMockApi();
  }
  root.render(<React.StrictMode>{tree}</React.StrictMode>);
};

void bootstrap();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
